import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  BLOCK_KINDS,
  BLOCK_KIND_LABELS,
  BLOCK_TEXT_FIELD,
  PAGE_TYPES,
  blockContentWriteSchema,
  emptyBlockContent,
  richTextFromPlain,
  type BlockContentWrite,
  type BlockKind,
} from '@seo/contracts';
import { appCodeOf, publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan, useSession } from '../lib/session';
import classes from './EntityPage.module.css';

interface FaqItemDraft {
  q: string;
  a: string;
}

interface FaqDraft {
  items: FaqItemDraft[];
  schemaOrg: boolean;
}

interface PaymentItemDraft {
  paymentMethodId: string;
  logo: {
    mediaFileId: string | null;
    url: string | null;
    alt: string;
    width: number | null;
    height: number | null;
  };
  name: string;
  type: string;
  minDeposit: string;
  maxDeposit: string;
  processingTime: string;
  withdrawalSupported: boolean;
}

interface PaymentDraft {
  groupByType: boolean;
  items: PaymentItemDraft[];
}

function paymentLogoOf(value: unknown, alt: string): PaymentItemDraft['logo'] {
  if (value === null || typeof value !== 'object') {
    return { mediaFileId: null, url: null, alt, width: null, height: null };
  }
  const logo = value as Record<string, unknown>;
  return {
    mediaFileId: typeof logo.mediaFileId === 'string' ? logo.mediaFileId : null,
    url: typeof logo.url === 'string' ? logo.url : null,
    alt: typeof logo.alt === 'string' ? logo.alt : alt,
    width: typeof logo.width === 'number' ? logo.width : null,
    height: typeof logo.height === 'number' ? logo.height : null,
  };
}

/**
 * Кількість слів у вмісті блока — для переліку в редакторі, не для §3-перевірок (ті рахує
 * сервер і пише в `sitePages.words`). Локальна копія логіки `countWords` із
 * `packages/core/src/services/pages.ts`: SPA імпортує лише contracts і tRPC-клієнт, тож
 * рахувати тут, а не тягнути `@seo/core` в бандл заради однієї функції.
 */
function blockWrite(blockKind: BlockKind, content: unknown): BlockContentWrite {
  return blockContentWriteSchema.parse({ blockKind, content });
}

function textWrite(
  block: { blockKind: BlockKind; content: unknown },
  plain: string,
): BlockContentWrite {
  const content = block.content as Record<string, unknown>;
  const field = BLOCK_TEXT_FIELD[block.blockKind];
  if (field === null) return blockWrite(block.blockKind, content);
  return blockWrite(block.blockKind, {
    ...content,
    [field]:
      block.blockKind === 'ai_text' || block.blockKind === 'manual_text'
        ? richTextFromPlain(plain)
        : plain,
  });
}

function notifyEditorError(error: unknown): void {
  notifications.show({
    color: 'red',
    title: appCodeOf(error) === 'VERSION_CONFLICT' ? 'Конфлікт версій' : 'Зміни не збережено',
    message: publicMessageOf(error),
  });
}

function wordCountOf(content: unknown): number {
  if (typeof content === 'string') {
    return content.trim().split(/\s+/u).filter(Boolean).length;
  }
  if (Array.isArray(content)) {
    return content.reduce<number>((sum, item) => sum + wordCountOf(item), 0);
  }
  if (content !== null && typeof content === 'object') {
    const rich = content as { plain?: unknown; words?: unknown };
    if (typeof rich.plain === 'string' && typeof rich.words === 'number') return rich.words;
    return Object.values(content).reduce<number>((sum, value) => sum + wordCountOf(value), 0);
  }
  return 0;
}

/**
 * Редактор сторінки (§4), acceptance scenarios 9 and 10.
 *
 * Everything on this screen serves one rule: an edit is protected. The lock badge, the
 * regeneration plan and the publish/revert controls all exist so an operator can see —
 * before pressing anything — what a regeneration is about to rewrite and what it will leave
 * alone.
 */
export function EditorPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canWrite = useCan('page_editor', 'write');
  const canDelete = useCan('page_editor', 'delete');
  const isSuperAdmin = useSession()?.role === 'super_admin';
  /**
   * Сайт і сторінка приходять із картки сайту — кліком по рядку сторінки, як у мокапі.
   *
   * Без них кнопка «Редактор» у картці вела на порожній екран, де обидва треба було вибрати
   * заново: двері були, але за ними починалося спочатку.
   */
  const search = useSearch({ from: '/authed/editor' });
  const [siteId, setSiteId] = useState<string | null>(search.siteId ?? null);
  const [pageId, setPageId] = useState<string | null>(search.pageId ?? null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [meta, setMeta] = useState<Record<string, string>>({});
  /** Вид блока з бібліотеки — до 14.08 екран умів додавати рівно FAQ і нічого більше. */
  const [newBlockKind, setNewBlockKind] = useState<string>('manual_text');
  const [newPageType, setNewPageType] = useState<string>('bonus');
  const [newPageUrl, setNewPageUrl] = useState('');
  const [anchorDraft, setAnchorDraft] = useState<Record<string, string>>({});
  /** Чернетка редактора FAQ per блок: items + schemaOrg, доки не збережено. */
  const [faqDraft, setFaqDraft] = useState<Record<string, FaqDraft>>({});
  /** Чернетка переліку платіжних методів per блок. */
  const [paymentDraft, setPaymentDraft] = useState<Record<string, PaymentDraft>>({});
  /** Вибір у селекторі «додати метод» — окремо per блок, бо блоків може бути кілька. */
  const [newPaymentMethodId, setNewPaymentMethodId] = useState<Record<string, string | null>>({});
  /** Блок, що чекає підтвердження видалення — щоб випадковий клік не стирав правку. */
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const sites = useQuery(
    trpc.sites.list.queryOptions({ pagination: { limit: 200, direction: 'desc' } }),
  );

  const pages = useQuery({
    ...trpc.pages.bySite.queryOptions({ siteId: siteId ?? '' }),
    enabled: siteId !== null,
  });

  const pageOptions = trpc.pages.byId.queryOptions({ id: pageId ?? '' });
  const page = useQuery({
    ...pageOptions,
    enabled: pageId !== null,
  });

  const blocks = page.data?.blocks ?? [];

  const plan = useQuery({
    ...trpc.pages.regenerationPlan.queryOptions({ id: pageId ?? '' }),
    enabled: pageId !== null,
  });

  const versions = useQuery({
    ...trpc.pages.versions.queryOptions({ id: pageId ?? '' }),
    enabled: pageId !== null,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.pages.pathKey() });
  };

  /**
   * A version conflict means one entity's expected version is stale — not every draft on the
   * screen. Scoping the rollback to the entity that actually conflicted keeps unrelated
   * unsaved work (another block's text, its FAQ table) intact instead of discarding it.
   */
  const handleEditorError = async (
    error: unknown,
    scope?: { kind: 'block'; blockId: string } | { kind: 'meta' },
  ): Promise<void> => {
    notifyEditorError(error);
    if (appCodeOf(error) !== 'VERSION_CONFLICT') return;
    if (scope?.kind === 'block') {
      const { blockId } = scope;
      const drop = <T,>(previous: Record<string, T>): Record<string, T> =>
        Object.fromEntries(Object.entries(previous).filter(([id]) => id !== blockId));
      setDraft(drop);
      setAnchorDraft(drop);
      setFaqDraft(drop);
      setPaymentDraft(drop);
      setNewPaymentMethodId(drop);
      setConfirmRemoveId((previous) => (previous === blockId ? null : previous));
    } else if (scope?.kind === 'meta') {
      setMeta({});
    }
    await invalidate();
  };

  /**
   * Черга сторінки — те, що робить асинхронну генерацію видимою.
   *
   * Постановка в чергу повертає лише «прийнято»; без цього переліку оператор не мав би
   * жодного способу дізнатися, що робота померла у DLQ, окрім як побачити назавжди порожню
   * сторінку.
   */
  const queue = useQuery({
    ...trpc.queue.list.queryOptions({
      kinds: ['generate_text'],
      ...(siteId === null ? {} : { siteId }),
      pagination: { limit: 20, direction: 'desc' },
    }),
    enabled: siteId !== null,
  });

  /**
   * Довідник платіжних методів для вибору в редакторі `payment_methods`-блока — той самий
   * запит, що й `ReferencePage`. Вантажиться лише коли на сторінці справді є такий блок:
   * решта сторінок не платить за перелік, який ніхто не побачить.
   */
  const paymentMethodsRef = useQuery({
    ...trpc.reference.paymentMethods.list.queryOptions({
      pagination: { limit: 200, direction: 'asc' },
    }),
    enabled: blocks.some((b) => b.blockKind === 'payment_methods'),
  });

  /**
   * Чернетки прив'язані до конкретного блока, тож успішне збереження мусить скинути саме їх —
   * інакше поле показує набране, а не те, що сервер справді прийняв (та сама причина, що й у
   * `setDisplay` нижче).
   */
  const editBlock = useMutation(
    trpc.pages.editBlock.mutationOptions({
      onError: (error, variables) =>
        handleEditorError(error, { kind: 'block', blockId: variables.blockId }),
      onSuccess: async (_result, variables) => {
        setDraft((previous) =>
          Object.fromEntries(
            Object.entries(previous).filter(([blockId]) => blockId !== variables.blockId),
          ),
        );
        setFaqDraft((previous) =>
          Object.fromEntries(
            Object.entries(previous).filter(([blockId]) => blockId !== variables.blockId),
          ),
        );
        setPaymentDraft((previous) =>
          Object.fromEntries(
            Object.entries(previous).filter(([blockId]) => blockId !== variables.blockId),
          ),
        );
        await invalidate();
      },
    }),
  );
  const unlock = useMutation(
    trpc.pages.unlockBlock.mutationOptions({
      onError: (error, variables) =>
        handleEditorError(error, { kind: 'block', blockId: variables.blockId }),
      onSuccess: invalidate,
    }),
  );
  const unlockMeta = useMutation(
    trpc.pages.unlockMeta.mutationOptions({
      onError: (error) => handleEditorError(error, { kind: 'meta' }),
      onSuccess: invalidate,
    }),
  );
  const addBlock = useMutation(
    trpc.pages.addBlock.mutationOptions({
      onError: (error) => handleEditorError(error),
      onSuccess: invalidate,
    }),
  );
  /**
   * Видалення блока. `removeBlock` існував у сервісі й у роутері без жодного викликача —
   * блок, доданий помилково, не можна було прибрати нічим, окрім прямого запиту до БД.
   */
  const removeBlock = useMutation(
    trpc.pages.removeBlock.mutationOptions({
      onError: (error, variables) =>
        handleEditorError(error, { kind: 'block', blockId: variables.blockId }),
      onSuccess: async () => {
        setConfirmRemoveId(null);
        await invalidate();
      },
    }),
  );
  /**
   * Скидає локальну чернетку якоря, щойно сервер її прийняв, — та сама причина, що й у
   * `saveMeta`: поле має показувати збережене, а не набране. Без цього екран казав би
   * «збережено» жодного разу не перечитавши, і нормалізація на сервері лишалась би невидимою.
   */
  const setDisplay = useMutation(
    trpc.pages.setBlockDisplay.mutationOptions({
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: pageOptions.queryKey, exact: true });
        const previous = queryClient.getQueryData(pageOptions.queryKey);
        queryClient.setQueryData(pageOptions.queryKey, (current) =>
          current === undefined
            ? current
            : {
                ...current,
                version: current.version + 1,
                blocks: current.blocks.map((block) =>
                  block.id === variables.blockId
                    ? {
                        ...block,
                        version: block.version + 1,
                        ...(variables.hidden === undefined ? {} : { hidden: variables.hidden }),
                        ...(variables.anchorId === undefined
                          ? {}
                          : { anchorId: variables.anchorId }),
                      }
                    : block,
                ),
              },
        );
        return { previous };
      },
      onError: async (error, variables, context) => {
        if (context?.previous !== undefined) {
          queryClient.setQueryData(pageOptions.queryKey, context.previous);
        }
        await handleEditorError(error, { kind: 'block', blockId: variables.blockId });
      },
      onSuccess: (_result, variables) => {
        // A hide request and an anchor edit can overlap. Only the request that actually
        // persisted an anchor owns that draft; otherwise a late hide response erases text
        // the operator typed while it was in flight and disables the save button.
        if (variables.anchorId === undefined) return;
        setAnchorDraft((previous) =>
          Object.fromEntries(
            Object.entries(previous).filter(([blockId]) => blockId !== variables.blockId),
          ),
        );
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey: pageOptions.queryKey, exact: true });
        await queryClient.invalidateQueries({ queryKey: trpc.pages.regenerationPlan.pathKey() });
      },
    }),
  );
  const createPage = useMutation(
    trpc.pages.create.mutationOptions({
      onError: (error) => handleEditorError(error),
      onSuccess: async (result) => {
        setNewPageUrl('');
        setPageId(result.id);
        await invalidate();
      },
    }),
  );
  /**
   * Постановка в чергу замість очікування на модель у HTTP-запиті.
   *
   * Синхронний `pages.generate` лишається поруч — він потрібен там, де відповідь миттєва
   * (мок-драйвер, тести). Але на справжньому OpenRouter сторінка з вісьмома AI-блоками — це
   * вісім послідовних викликів моделі в одному запиті, і проксі розірве зʼєднання раніше,
   * ніж прийде відповідь: робота триває, а екран каже, що впала.
   */
  const enqueue = useMutation(
    trpc.queue.enqueueGeneration.mutationOptions({
      onError: (error) => handleEditorError(error),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.queue.pathKey() });
      },
    }),
  );
  const reorder = useMutation(
    trpc.pages.reorderBlocks.mutationOptions({
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: pageOptions.queryKey, exact: true });
        const previous = queryClient.getQueryData(pageOptions.queryKey);
        queryClient.setQueryData(pageOptions.queryKey, (current) => {
          if (current === undefined) return current;
          const byId = new Map(current.blocks.map((block) => [block.id, block]));
          const ordered = variables.orderedBlockIds.flatMap((id, sortOrder) => {
            const block = byId.get(id);
            return block === undefined ? [] : [{ ...block, sortOrder, version: block.version + 1 }];
          });
          return { ...current, version: current.version + 1, blocks: ordered };
        });
        return { previous };
      },
      onError: async (error, _variables, context) => {
        if (context?.previous !== undefined) {
          queryClient.setQueryData(pageOptions.queryKey, context.previous);
        }
        await handleEditorError(error);
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey: pageOptions.queryKey, exact: true });
      },
    }),
  );
  const generate = useMutation(
    trpc.pages.generate.mutationOptions({
      onError: (error) => handleEditorError(error),
      onSuccess: invalidate,
    }),
  );
  const publish = useMutation(
    trpc.pages.publish.mutationOptions({
      onError: (error) => handleEditorError(error),
      onSuccess: invalidate,
    }),
  );
  const revert = useMutation(
    trpc.pages.revert.mutationOptions({
      onError: (error) => handleEditorError(error),
      onSuccess: invalidate,
    }),
  );
  /**
   * Drops the local draft once the server has the value, so the field shows what was
   * actually stored. Keeping the draft would leave the input showing text the server may
   * have normalized or rejected — a screen that says "saved" without ever reading back.
   */
  const saveMeta = useMutation(
    trpc.pages.updateMeta.mutationOptions({
      onError: (error) => handleEditorError(error, { kind: 'meta' }),
      onSuccess: async () => {
        setMeta({});
        await invalidate();
      },
    }),
  );

  /**
   * Поле, яке рендерер справді читає з цього виду блока.
   *
   * До 14.08 редактор клав текст у `text` завжди — а `renderBlock` для `faq`, `bonus_table`,
   * `payment_methods`, `bonus_banner` і `cta_button` читає структуру й ігнорує `text`
   * повністю. Єдина кнопка, яку екран мав («Додати FAQ-блок»), створювала блок, що ніколи не
   * потрапляв у HTML.
   */
  const textOf = (block: { id: string; blockKind: string; content: unknown }): string => {
    const edited = draft[block.id];
    if (edited !== undefined) return edited;
    const field = BLOCK_TEXT_FIELD[block.blockKind as (typeof BLOCK_KINDS)[number]];
    if (field === null) return '';
    const content = block.content as Record<string, unknown> | null;
    const value = content?.[field];
    if (block.blockKind === 'ai_text' || block.blockKind === 'manual_text') {
      const rich = value as { plain?: unknown } | undefined;
      return typeof rich?.plain === 'string' ? rich.plain : '';
    }
    return typeof value === 'string' ? value : '';
  };

  /**
   * Поточний стан FAQ-блока: чернетка, якщо оператор уже щось змінив, інакше — те, що
   * прийшло з сервера. `faq` — один із пʼяти видів, які `BLOCK_TEXT_FIELD` позначає `null`:
   * рендерер читає `items`, і порожній перелік — це блок, якого немає в HTML.
   */
  const faqOf = (block: { id: string; content: unknown }): FaqDraft => {
    const existing = faqDraft[block.id];
    if (existing !== undefined) return existing;
    const content = block.content as { items?: unknown; schemaOrg?: boolean } | null;
    const items = Array.isArray(content?.items)
      ? (content.items as { q?: unknown; a?: unknown }[]).map((item) => ({
          q: typeof item.q === 'string' ? item.q : '',
          a: typeof item.a === 'string' ? item.a : '',
        }))
      : [];
    return { items, schemaOrg: content?.schemaOrg ?? true };
  };

  /** Той самий принцип для `payment_methods`: чернетка або нормалізований вміст сервера. */
  const paymentOf = (block: { id: string; content: unknown }): PaymentDraft => {
    const existing = paymentDraft[block.id];
    if (existing !== undefined) return existing;
    const content = block.content as { groupByType?: boolean; items?: unknown } | null;
    const items = Array.isArray(content?.items)
      ? (content.items as Record<string, unknown>[]).map((item) => ({
          paymentMethodId: typeof item.paymentMethodId === 'string' ? item.paymentMethodId : '',
          name: typeof item.name === 'string' ? item.name : '',
          logo: paymentLogoOf(item.logo, typeof item.name === 'string' ? item.name : ''),
          type: typeof item.type === 'string' ? item.type : '',
          minDeposit: typeof item.minDeposit === 'string' ? item.minDeposit : '',
          maxDeposit: typeof item.maxDeposit === 'string' ? item.maxDeposit : '',
          processingTime: typeof item.processingTime === 'string' ? item.processingTime : '',
          withdrawalSupported: item.withdrawalSupported !== false,
        }))
      : [];
    return { groupByType: content?.groupByType ?? true, items };
  };

  return (
    <Stack gap="md">
      <Group gap="md" align="flex-end">
        <Select
          label="Сайт"
          data={(sites.data?.items ?? []).map((s) => ({ value: s.id, label: s.domain }))}
          value={siteId}
          onChange={(value) => {
            setSiteId(value);
            setPageId(null);
            setMeta({});
            setDraft({});
            setFaqDraft({});
            setPaymentDraft({});
            setAnchorDraft({});
            setNewPaymentMethodId({});
            setConfirmRemoveId(null);
          }}
          searchable
          data-testid="editor-site"
          style={{ width: 240 }}
        />
        <Select
          label="Сторінка"
          data={(pages.data ?? []).map((p) => ({ value: p.id, label: p.url }))}
          value={pageId}
          onChange={(value) => {
            // The drafts belong to the page that was open; carrying them across would show
            // the previous page's title on this one.
            setPageId(value);
            setMeta({});
            setDraft({});
            setFaqDraft({});
            setPaymentDraft({});
            setAnchorDraft({});
            setNewPaymentMethodId({});
            setConfirmRemoveId(null);
          }}
          disabled={siteId === null}
          data-testid="editor-page"
          style={{ width: 200 }}
        />
        <Button
          size="xs"
          variant="default"
          disabled={pageId === null}
          loading={generate.isPending}
          data-testid="editor-generate"
          onClick={() => {
            if (pageId !== null) generate.mutate({ pageId });
          }}
        >
          Перегенерувати
        </Button>
        {/*
         * Через чергу — окремою кнопкою, а не замість синхронної.
         *
         * Синхронний прогін лишається для мок-драйвера й тестів, де відповідь миттєва. На
         * справжній моделі сторінка з вісьмома AI-блоками — вісім послідовних викликів в
         * одному HTTP-запиті: проксі розірве зʼєднання раніше, ніж прийде відповідь.
         */}
        <Button
          size="xs"
          variant="default"
          disabled={pageId === null}
          loading={enqueue.isPending}
          data-testid="editor-enqueue"
          onClick={() => {
            if (pageId !== null) enqueue.mutate({ pageId });
          }}
        >
          У чергу
        </Button>
        <Button
          size="xs"
          disabled={pageId === null || editBlock.isPending}
          loading={publish.isPending}
          data-testid="editor-publish"
          onClick={() => {
            if (pageId !== null && page.data !== undefined) {
              publish.mutate({ id: pageId, expectedVersion: page.data.version });
            }
          }}
        >
          Опублікувати
        </Button>
      </Group>

      {/*
       * Створення сторінки поза збіркою.
       *
       * Рядки `site_pages` зʼявлялись **лише всередині `assembleBuild`**: додати сторінку до
       * вже зібраного сайту не можна було жодним запитом, тож «додати сторінку умов»
       * дорівнювало «перезібрати сайт» — переписати весь його контент заради однієї адреси.
       */}
      {siteId === null ? null : (
        <Box className={classes.panel} data-testid="editor-new-page">
          <Group gap="sm" align="flex-end">
            <Select
              size="xs"
              label="Тип нової сторінки"
              searchable
              allowDeselect={false}
              data={[...PAGE_TYPES]}
              value={newPageType}
              data-testid="new-page-type"
              style={{ width: 170 }}
              onChange={(value) => {
                setNewPageType(value ?? 'bonus');
              }}
            />
            <TextInput
              size="xs"
              label="Адреса"
              placeholder="/bonus/"
              value={newPageUrl}
              data-testid="new-page-url"
              style={{ width: 170 }}
              onChange={(event) => {
                setNewPageUrl(event.currentTarget.value);
              }}
            />
            <Button
              size="compact-xs"
              loading={createPage.isPending}
              disabled={newPageUrl.trim() === ''}
              data-testid="new-page-create"
              onClick={() => {
                createPage.mutate({
                  siteId,
                  pageType: newPageType as (typeof PAGE_TYPES)[number],
                  url: newPageUrl.trim(),
                });
              }}
            >
              Створити сторінку
            </Button>
          </Group>
          {createPage.error === null ? null : (
            <Text className={classes.danger} mt={6} data-testid="new-page-error" role="alert">
              {publicMessageOf(createPage.error)}
            </Text>
          )}
        </Box>
      )}

      {pageId === null ? (
        <Box className={classes.panel} data-testid="editor-no-page">
          <Text className={classes.dim}>Оберіть сайт і сторінку.</Text>
        </Box>
      ) : page.isPending ? (
        <Loader size="sm" />
      ) : (
        <>
          {page.data === undefined ? null : (
            <Box className={classes.panel} data-testid="editor-meta">
              <Group gap={8} mb={6}>
                <Text className={classes.panelTitle}>Мета сторінки</Text>
                {page.data.manualLock ? <Badge color="amber">залочено вручну</Badge> : null}
                {page.data.manualLock && isSuperAdmin ? (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    loading={unlockMeta.isPending}
                    data-testid="meta-unlock"
                    onClick={() => {
                      unlockMeta.mutate({
                        pageId: page.data.id,
                        expectedVersion: page.data.version,
                      });
                    }}
                  >
                    Примусово перегенерувати
                  </Button>
                ) : null}
              </Group>
              <Stack gap={8}>
                <TextInput
                  label="Title"
                  size="xs"
                  data-testid="meta-title"
                  value={meta.title ?? page.data.title ?? ''}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setMeta((current) => ({ ...current, title: value }));
                  }}
                />
                <TextInput
                  label="Description"
                  size="xs"
                  data-testid="meta-description"
                  value={meta.description ?? page.data.description ?? ''}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setMeta((current) => ({ ...current, description: value }));
                  }}
                />
                <TextInput
                  label="H1"
                  size="xs"
                  data-testid="meta-h1"
                  value={meta.h1 ?? page.data.h1 ?? ''}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setMeta((current) => ({ ...current, h1: value }));
                  }}
                />
                <Group gap={8}>
                  <Button
                    size="xs"
                    loading={saveMeta.isPending}
                    data-testid="meta-save"
                    onClick={() => {
                      if (pageId === null) return;
                      const title = meta.title ?? page.data?.title ?? '';
                      const description = meta.description ?? page.data?.description ?? '';
                      const h1 = meta.h1 ?? page.data?.h1 ?? '';
                      saveMeta.mutate({
                        id: pageId,
                        expectedVersion: page.data.version,
                        ...(title === '' ? {} : { title }),
                        ...(description === '' ? {} : { description }),
                        ...(h1 === '' ? {} : { h1 }),
                      });
                    }}
                  >
                    Зберегти мету
                  </Button>
                  {saveMeta.error === null ? null : (
                    <Text className={classes.danger} data-testid="meta-error">
                      {publicMessageOf(saveMeta.error)}
                    </Text>
                  )}
                </Group>
              </Stack>
            </Box>
          )}

          {plan.data === undefined ? null : (
            <Box className={classes.panel} data-testid="editor-plan">
              <Text className={classes.panelTitle} mb={6}>
                Що зробить перегенерація
              </Text>
              <Group gap="lg">
                <Text className={classes.dim}>
                  перепише:{' '}
                  <span className={classes.mono} data-testid="plan-regenerate">
                    {plan.data.willRegenerate.length}
                  </span>
                </Text>
                <Text className={classes.dim}>
                  не чіпатиме:{' '}
                  <span className={classes.mono} data-testid="plan-skip">
                    {plan.data.skipped.length}
                  </span>
                </Text>
                <Text className={classes.dim} data-testid="plan-locked">
                  залочено:{' '}
                  <span className={classes.mono}>
                    {plan.data.skipped.filter((s) => s.reason === 'manual_lock').length}
                  </span>
                </Text>
              </Group>
            </Box>
          )}

          {generate.error === null ? null : (
            <Box className={classes.panel} data-testid="editor-gen-error">
              <Text className={classes.danger} data-testid="gen-error">
                {publicMessageOf(generate.error)}
              </Text>
            </Box>
          )}

          {generate.data === undefined ? null : (
            <Box className={classes.panel} data-testid="editor-gen-result">
              <Group gap="lg">
                <Text className={classes.dim}>
                  написано:{' '}
                  <span className={classes.mono} data-testid="gen-written">
                    {generate.data.written}
                  </span>
                </Text>
                <Text className={classes.dim}>
                  пропущено:{' '}
                  <span className={classes.mono} data-testid="gen-skipped">
                    {generate.data.skipped}
                  </span>
                </Text>
                <Text
                  className={generate.data.failedChecks > 0 ? classes.danger : classes.dim}
                  data-testid="gen-failed"
                >
                  не пройшло перевірки: {generate.data.failedChecks}
                </Text>
              </Group>
            </Box>
          )}

          <Stack gap="sm" data-testid="editor-blocks">
            {blocks.map((block, index) => (
              <Box
                className={classes.panel}
                key={block.id}
                data-testid={`block-${block.blockKind}`}
              >
                <Group justify="space-between" mb={6}>
                  <Group gap={8}>
                    <span className={classes.mono}>{block.sortOrder}</span>
                    <Badge size="sm" variant="default">
                      {block.blockKind}
                    </Badge>
                    <Badge size="sm" variant="default">
                      {block.mode}
                    </Badge>
                    {block.manualLock ? (
                      <Badge size="sm" color="amber" data-testid={`lock-${block.blockKind}`}>
                        залочено
                      </Badge>
                    ) : null}
                    {/* Схований блок лишається в редакторі — і мусить виглядати схованим. */}
                    {block.hidden ? (
                      <Badge size="sm" color="slate" data-testid={`hidden-badge-${block.id}`}>
                        сховано
                      </Badge>
                    ) : null}
                    {block.anchorId === null ? null : (
                      <Badge size="sm" variant="default" data-testid={`anchor-badge-${block.id}`}>
                        #{block.anchorId}
                      </Badge>
                    )}
                  </Group>
                  <Group gap={6}>
                    {block.manualLock && isSuperAdmin ? (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        data-testid={`unlock-${block.blockKind}`}
                        onClick={() => {
                          unlock.mutate({ blockId: block.id, expectedVersion: block.version });
                        }}
                      >
                        Розлочити
                      </Button>
                    ) : null}
                    {index === 0 ? null : (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        disabled={reorder.isPending}
                        data-testid={`up-${block.blockKind}`}
                        onClick={() => {
                          const swapped = blocks.map((b) => b.id);
                          const previous = swapped[index - 1];
                          const current = swapped[index];
                          if (previous === undefined || current === undefined) return;
                          swapped[index - 1] = current;
                          swapped[index] = previous;
                          if (pageId !== null && page.data !== undefined) {
                            reorder.mutate({
                              pageId,
                              expectedVersion: page.data.version,
                              orderedBlockIds: swapped,
                            });
                          }
                        }}
                      >
                        ↑
                      </Button>
                    )}
                    {/* Порядок униз — раніше екран умів рухати блок лише вгору. */}
                    {index === blocks.length - 1 ? null : (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        disabled={reorder.isPending}
                        data-testid={`down-${block.id}`}
                        onClick={() => {
                          const swapped = blocks.map((b) => b.id);
                          const next = swapped[index + 1];
                          const current = swapped[index];
                          if (next === undefined || current === undefined) return;
                          swapped[index + 1] = current;
                          swapped[index] = next;
                          if (pageId !== null && page.data !== undefined) {
                            reorder.mutate({
                              pageId,
                              expectedVersion: page.data.version,
                              orderedBlockIds: swapped,
                            });
                          }
                        }}
                      >
                        ↓
                      </Button>
                    )}
                    {/*
                     * Видалення — двоклікове, як і решта деструктивних дій на екрані
                     * (шаблон, гест-пост): перший клік просить підтвердження, другий стирає
                     * блок разом із будь-якою людською правкою в ньому. `removeBlock` існував
                     * у сервісі й у роутері без жодного викликача з інтерфейсу.
                     */}
                    {canDelete ? (
                      confirmRemoveId === block.id ? (
                        <>
                          <Button
                            size="compact-xs"
                            color="rose"
                            loading={removeBlock.isPending}
                            data-testid={`remove-confirm-${block.id}`}
                            onClick={() => {
                              removeBlock.mutate({
                                blockId: block.id,
                                expectedVersion: block.version,
                              });
                            }}
                          >
                            Видалити
                          </Button>
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            data-testid={`remove-cancel-${block.id}`}
                            onClick={() => {
                              setConfirmRemoveId(null);
                              removeBlock.reset();
                            }}
                          >
                            Скасувати
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="rose"
                          data-testid={`remove-${block.id}`}
                          onClick={() => {
                            setConfirmRemoveId(block.id);
                          }}
                        >
                          Видалити
                        </Button>
                      )
                    ) : null}
                  </Group>
                </Group>

                {/* Скільки слів, коли згенеровано — стан блока, який раніше ніде не було видно. */}
                <Group gap={10} mb={6} data-testid={`meta-${block.id}`}>
                  <Text className={classes.dim} data-testid={`words-${block.id}`}>
                    слів: <span className={classes.mono}>{wordCountOf(block.content)}</span>
                  </Text>
                  <Text className={classes.dim} data-testid={`generated-${block.id}`}>
                    {block.generatedAt === null
                      ? 'ще не генерувався'
                      : `згенеровано: ${new Date(block.generatedAt).toLocaleString('uk-UA', { timeZone: 'Europe/Kyiv' })}`}
                  </Text>
                </Group>

                {/* Перевірки генерації — `checksJson` пишеться з першого дня й ніде не показувався. */}
                {block.checks === null || typeof block.checks !== 'object' ? null : (
                  <Stack gap={2} mb={6} data-testid={`checks-${block.id}`}>
                    {Object.entries(block.checks as Record<string, unknown>).map(
                      ([name, value]) => (
                        <Text
                          key={name}
                          className={value === true ? classes.dim : classes.danger}
                          data-testid={`check-${block.id}-${name}`}
                        >
                          {value === true ? '✓' : '✕'} {name}
                        </Text>
                      ),
                    )}
                  </Stack>
                )}

                {confirmRemoveId === block.id && removeBlock.error !== null ? (
                  <Text
                    className={classes.danger}
                    mb={6}
                    data-testid={`remove-error-${block.id}`}
                    role="alert"
                  >
                    {publicMessageOf(removeBlock.error)}
                  </Text>
                ) : null}

                {block.blockKind === 'faq' ? (
                  /*
                   * FAQ — питання/відповідь, а не текст: `renderBlock` читає `items` і на
                   * порожньому переліку повертає порожній рядок. Це один із двох видів, що
                   * найчастіше лишались порожніми й через це мовчки зникали зі сторінки.
                   */
                  <Stack gap={8} data-testid={`faq-editor-${block.id}`}>
                    {faqOf(block).items.length === 0 ? (
                      <Text className={classes.dim} data-testid={`faq-empty-${block.id}`}>
                        Питань немає — блок згорнеться порожнім на сторінці
                      </Text>
                    ) : (
                      faqOf(block).items.map((item, itemIndex) => (
                        <Stack
                          key={String(itemIndex)}
                          gap={4}
                          data-testid={`faq-item-${block.id}-${String(itemIndex)}`}
                        >
                          <Group gap={6}>
                            <TextInput
                              size="xs"
                              placeholder="питання"
                              value={item.q}
                              disabled={!canWrite}
                              data-testid={`faq-q-${block.id}-${String(itemIndex)}`}
                              style={{ flex: 1 }}
                              onChange={(event) => {
                                const { value } = event.currentTarget;
                                const current = faqOf(block);
                                const items = current.items.map((it, i) =>
                                  i === itemIndex ? { ...it, q: value } : it,
                                );
                                setFaqDraft((previous) => ({
                                  ...previous,
                                  [block.id]: { ...current, items },
                                }));
                              }}
                            />
                            {canWrite ? (
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                color="rose"
                                data-testid={`faq-remove-${block.id}-${String(itemIndex)}`}
                                onClick={() => {
                                  const current = faqOf(block);
                                  const items = current.items.filter((_, i) => i !== itemIndex);
                                  setFaqDraft((previous) => ({
                                    ...previous,
                                    [block.id]: { ...current, items },
                                  }));
                                }}
                              >
                                прибрати
                              </Button>
                            ) : null}
                          </Group>
                          <Textarea
                            // Не `autosize`: він міряє елемент через ResizeObserver, якого немає
                            // в DOM компонентного тесту (див. коментар у ParsePanel.tsx).
                            rows={2}
                            placeholder="відповідь"
                            value={item.a}
                            disabled={!canWrite}
                            data-testid={`faq-a-${block.id}-${String(itemIndex)}`}
                            onChange={(event) => {
                              const { value } = event.currentTarget;
                              const current = faqOf(block);
                              const items = current.items.map((it, i) =>
                                i === itemIndex ? { ...it, a: value } : it,
                              );
                              setFaqDraft((previous) => ({
                                ...previous,
                                [block.id]: { ...current, items },
                              }));
                            }}
                          />
                        </Stack>
                      ))
                    )}
                    {canWrite ? (
                      <Group gap={8}>
                        <Button
                          size="compact-xs"
                          variant="default"
                          data-testid={`faq-add-${block.id}`}
                          onClick={() => {
                            const current = faqOf(block);
                            const items = [...current.items, { q: '', a: '' }];
                            setFaqDraft((previous) => ({
                              ...previous,
                              [block.id]: { ...current, items },
                            }));
                          }}
                        >
                          + питання
                        </Button>
                        <Switch
                          size="xs"
                          label="schema.org"
                          checked={faqOf(block).schemaOrg}
                          data-testid={`faq-schema-${block.id}`}
                          onChange={(event) => {
                            const checked = event.currentTarget.checked;
                            const current = faqOf(block);
                            setFaqDraft((previous) => ({
                              ...previous,
                              [block.id]: { ...current, schemaOrg: checked },
                            }));
                          }}
                        />
                        <Button
                          size="compact-xs"
                          loading={editBlock.isPending}
                          disabled={faqDraft[block.id] === undefined}
                          data-testid={`faq-save-${block.id}`}
                          onClick={() => {
                            const current = faqOf(block);
                            editBlock.mutate({
                              ...blockWrite('faq', {
                                items: current.items,
                                schemaOrg: current.schemaOrg,
                              }),
                              blockId: block.id,
                              expectedVersion: block.version,
                            });
                          }}
                        >
                          Зберегти FAQ
                        </Button>
                      </Group>
                    ) : null}
                  </Stack>
                ) : block.blockKind === 'payment_methods' ? (
                  /*
                   * Платіжні методи — другий вид, що найчастіше лишався порожнім. Додавання
                   * бере методи з довідника (`reference.paymentMethods`), а не вигадує id.
                   */
                  <Stack gap={8} data-testid={`payment-editor-${block.id}`}>
                    <Switch
                      size="xs"
                      label="групувати за типом"
                      checked={paymentOf(block).groupByType}
                      disabled={!canWrite}
                      data-testid={`pm-group-${block.id}`}
                      onChange={(event) => {
                        const checked = event.currentTarget.checked;
                        const current = paymentOf(block);
                        setPaymentDraft((previous) => ({
                          ...previous,
                          [block.id]: { ...current, groupByType: checked },
                        }));
                      }}
                    />
                    {paymentOf(block).items.length === 0 ? (
                      <Text className={classes.dim} data-testid={`pm-empty-${block.id}`}>
                        Методів немає — блок згорнеться порожнім на сторінці
                      </Text>
                    ) : (
                      paymentOf(block).items.map((item, itemIndex) => (
                        <Group
                          key={String(itemIndex)}
                          gap={6}
                          data-testid={`pm-item-${block.id}-${String(itemIndex)}`}
                        >
                          <Badge size="sm" variant="default">
                            {item.name || '—'}
                          </Badge>
                          <Text className={classes.dim}>{item.type}</Text>
                          <TextInput
                            size="xs"
                            placeholder="мін. депозит"
                            value={item.minDeposit}
                            disabled={!canWrite}
                            data-testid={`pm-min-${block.id}-${String(itemIndex)}`}
                            style={{ width: 100 }}
                            onChange={(event) => {
                              const { value } = event.currentTarget;
                              const current = paymentOf(block);
                              const items = current.items.map((it, i) =>
                                i === itemIndex ? { ...it, minDeposit: value } : it,
                              );
                              setPaymentDraft((previous) => ({
                                ...previous,
                                [block.id]: { ...current, items },
                              }));
                            }}
                          />
                          <TextInput
                            size="xs"
                            placeholder="макс. депозит"
                            value={item.maxDeposit}
                            disabled={!canWrite}
                            data-testid={`pm-max-${block.id}-${String(itemIndex)}`}
                            style={{ width: 100 }}
                            onChange={(event) => {
                              const { value } = event.currentTarget;
                              const current = paymentOf(block);
                              const items = current.items.map((it, i) =>
                                i === itemIndex ? { ...it, maxDeposit: value } : it,
                              );
                              setPaymentDraft((previous) => ({
                                ...previous,
                                [block.id]: { ...current, items },
                              }));
                            }}
                          />
                          <TextInput
                            size="xs"
                            placeholder="час обробки"
                            value={item.processingTime}
                            disabled={!canWrite}
                            data-testid={`pm-processing-${block.id}-${String(itemIndex)}`}
                            style={{ width: 100 }}
                            onChange={(event) => {
                              const { value } = event.currentTarget;
                              const current = paymentOf(block);
                              const items = current.items.map((it, i) =>
                                i === itemIndex ? { ...it, processingTime: value } : it,
                              );
                              setPaymentDraft((previous) => ({
                                ...previous,
                                [block.id]: { ...current, items },
                              }));
                            }}
                          />
                          <Switch
                            size="xs"
                            label="вивід"
                            checked={item.withdrawalSupported}
                            disabled={!canWrite}
                            data-testid={`pm-withdrawal-${block.id}-${String(itemIndex)}`}
                            onChange={(event) => {
                              const checked = event.currentTarget.checked;
                              const current = paymentOf(block);
                              const items = current.items.map((it, i) =>
                                i === itemIndex ? { ...it, withdrawalSupported: checked } : it,
                              );
                              setPaymentDraft((previous) => ({
                                ...previous,
                                [block.id]: { ...current, items },
                              }));
                            }}
                          />
                          {canWrite ? (
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              color="rose"
                              data-testid={`pm-remove-${block.id}-${String(itemIndex)}`}
                              onClick={() => {
                                const current = paymentOf(block);
                                const items = current.items.filter((_, i) => i !== itemIndex);
                                setPaymentDraft((previous) => ({
                                  ...previous,
                                  [block.id]: { ...current, items },
                                }));
                              }}
                            >
                              прибрати
                            </Button>
                          ) : null}
                        </Group>
                      ))
                    )}
                    {paymentMethodsRef.error !== null ? (
                      <Text className={classes.danger} data-testid={`pm-ref-error-${block.id}`}>
                        {publicMessageOf(paymentMethodsRef.error)}
                      </Text>
                    ) : canWrite ? (
                      <Group gap={6}>
                        <Select
                          size="xs"
                          searchable
                          placeholder="метод із довідника"
                          data={(paymentMethodsRef.data?.items ?? [])
                            .filter(
                              (pm) =>
                                !paymentOf(block).items.some((it) => it.paymentMethodId === pm.id),
                            )
                            .map((pm) => ({ value: pm.id, label: `${pm.name} · ${pm.type}` }))}
                          value={newPaymentMethodId[block.id] ?? null}
                          data-testid={`pm-add-select-${block.id}`}
                          style={{ width: 220 }}
                          onChange={(value) => {
                            setNewPaymentMethodId((previous) => ({
                              ...previous,
                              [block.id]: value,
                            }));
                          }}
                        />
                        <Button
                          size="compact-xs"
                          variant="default"
                          disabled={(newPaymentMethodId[block.id] ?? null) === null}
                          data-testid={`pm-add-${block.id}`}
                          onClick={() => {
                            const chosenId = newPaymentMethodId[block.id];
                            const chosen = (paymentMethodsRef.data?.items ?? []).find(
                              (pm) => pm.id === chosenId,
                            );
                            if (chosen === undefined) return;
                            const current = paymentOf(block);
                            setPaymentDraft((previous) => ({
                              ...previous,
                              [block.id]: {
                                ...current,
                                items: [
                                  ...current.items,
                                  {
                                    paymentMethodId: chosen.id,
                                    name: chosen.name,
                                    type: chosen.type,
                                    logo: paymentLogoOf(null, chosen.name),
                                    minDeposit: '',
                                    maxDeposit: '',
                                    processingTime: chosen.processingTime ?? '',
                                    withdrawalSupported: chosen.withdrawalSupported,
                                  },
                                ],
                              },
                            }));
                            setNewPaymentMethodId((previous) => ({
                              ...previous,
                              [block.id]: null,
                            }));
                          }}
                        >
                          + метод
                        </Button>
                        <Button
                          size="compact-xs"
                          loading={editBlock.isPending}
                          disabled={paymentDraft[block.id] === undefined}
                          data-testid={`pm-save-${block.id}`}
                          onClick={() => {
                            const current = paymentOf(block);
                            editBlock.mutate({
                              ...blockWrite('payment_methods', {
                                groupByType: current.groupByType,
                                items: current.items.map((item) => ({
                                  paymentMethodId: item.paymentMethodId,
                                  name: item.name,
                                  type: item.type,
                                  logo: item.logo,
                                  minDeposit: item.minDeposit,
                                  maxDeposit: item.maxDeposit,
                                  processingTime: item.processingTime,
                                  withdrawalSupported: item.withdrawalSupported,
                                })),
                              }),
                              blockId: block.id,
                              expectedVersion: block.version,
                            });
                          }}
                        >
                          Зберегти
                        </Button>
                      </Group>
                    ) : null}
                  </Stack>
                ) : BLOCK_TEXT_FIELD[block.blockKind] === null ? (
                  /*
                   * Не порожнє поле, а сказана вголос причина: цей вид рендериться зі
                   * структури, і текст, покладений у нього, не доїхав би в HTML. Раніше
                   * текстове поле стояло тут теж — і мовчки нікуди не вело.
                   */
                  <Text className={classes.dim} data-testid={`structural-${block.blockKind}`}>
                    Цей вид блока збирається з даних сутності — текстом він не редагується
                  </Text>
                ) : (
                  <>
                    <Textarea
                      // Не `autosize`: він міряє елемент через ResizeObserver, якого немає в
                      // DOM компонентного тесту (див. коментар у ParsePanel.tsx). Цей рядок
                      // існував так з першого дня редактора й валив рендер будь-якого тесту
                      // екрана, доки на ньому не зʼявився перший компонентний тест.
                      rows={4}
                      value={textOf(block)}
                      data-testid={`text-${block.blockKind}`}
                      onChange={(e) => {
                        // Read the event before the updater runs: React calls the updater during
                        // the next render, when `e.currentTarget` is already null.
                        const { value } = e.currentTarget;
                        setDraft((prev) => ({ ...prev, [block.id]: value }));
                      }}
                    />
                    <Group mt={6}>
                      <Button
                        size="compact-xs"
                        disabled={draft[block.id] === undefined}
                        loading={editBlock.isPending}
                        data-testid={`save-${block.blockKind}`}
                        onClick={() => {
                          editBlock.mutate({
                            ...textWrite(
                              { blockKind: block.blockKind, content: block.content },
                              draft[block.id] ?? '',
                            ),
                            blockId: block.id,
                            expectedVersion: block.version,
                          });
                        }}
                      >
                        Зберегти
                      </Button>
                      <Text className={classes.dim}>
                        правка лочить блок — перегенерація його не перепише
                      </Text>
                    </Group>
                  </>
                )}

                {/*
                 * Інспектор блока: показ, якір і перегенерація саме цього блока.
                 *
                 * `hidden` і `anchor_id` писались у знімок версії при публікації й не мали
                 * жодної процедури запису — «сховати блок» було можливо тільки видаливши
                 * його, після чого перегенерація повертала його зі складу шаблону.
                 */}
                <Group gap={8} mt={6} data-testid={`inspector-${block.id}`}>
                  <Switch
                    size="xs"
                    label="сховати"
                    checked={block.hidden}
                    disabled={setDisplay.isPending}
                    data-testid={`hidden-${block.id}`}
                    onChange={(event) => {
                      const { checked } = event.currentTarget;
                      setDisplay.mutate({
                        blockId: block.id,
                        expectedVersion: block.version,
                        hidden: checked,
                      });
                    }}
                  />
                  <TextInput
                    size="xs"
                    placeholder="якір"
                    value={anchorDraft[block.id] ?? block.anchorId ?? ''}
                    data-testid={`anchor-${block.id}`}
                    style={{ width: 150 }}
                    onChange={(event) => {
                      const { value } = event.currentTarget;
                      setAnchorDraft((previous) => ({ ...previous, [block.id]: value }));
                    }}
                  />
                  <Button
                    size="compact-xs"
                    variant="default"
                    disabled={anchorDraft[block.id] === undefined || setDisplay.isPending}
                    data-testid={`anchor-save-${block.id}`}
                    onClick={() => {
                      const value = (anchorDraft[block.id] ?? '').trim();
                      setDisplay.mutate({
                        blockId: block.id,
                        expectedVersion: block.version,
                        anchorId: value === '' ? null : value,
                      });
                    }}
                  >
                    Зберегти якір
                  </Button>
                  {/*
                   * Перегенерація одного блока. Без неї одиницею був цілий екран: щоб
                   * переписати один невдалий абзац, оператор платив за виклик моделі на
                   * кожен AI-блок сторінки й отримував новий текст скрізь.
                   */}
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    disabled={block.manualLock || block.mode !== 'ai'}
                    loading={enqueue.isPending}
                    data-testid={`regen-${block.id}`}
                    onClick={() => {
                      if (pageId !== null) {
                        enqueue.mutate({ pageId, blockIds: [block.id] });
                      }
                    }}
                  >
                    Перегенерувати блок
                  </Button>
                  {block.manualLock && block.blockKind === 'ai_text' && isSuperAdmin ? (
                    <Button
                      size="compact-xs"
                      color="rose"
                      variant="subtle"
                      loading={generate.isPending}
                      data-testid={`force-regen-${block.id}`}
                      onClick={() => {
                        if (pageId !== null) {
                          generate.mutate({
                            pageId,
                            blockIds: [block.id],
                            forceUnlock: true,
                          });
                        }
                      }}
                    >
                      Примусово перегенерувати
                    </Button>
                  ) : null}
                </Group>

                {setDisplay.error === null ? null : (
                  <Text className={classes.danger} mt={4} data-testid={`display-error-${block.id}`}>
                    {publicMessageOf(setDisplay.error)}
                  </Text>
                )}
              </Box>
            ))}
          </Stack>

          {/*
           * Бібліотека з двадцяти пʼяти видів замість однієї зашитої кнопки «Додати
           * FAQ-блок». `BLOCK_KINDS` існував з першого дня; екран пропонував із нього
           * рівно один рядок, тож двадцять чотири види блоків не можна було поставити на
           * сторінку взагалі.
           */}
          <Box className={classes.panel} data-testid="editor-add">
            <Group gap="sm" align="flex-end">
              <Select
                size="xs"
                label="Вид блока"
                searchable
                allowDeselect={false}
                data={BLOCK_KINDS.map((value) => ({
                  value,
                  label: BLOCK_KIND_LABELS[value] ?? value,
                }))}
                value={newBlockKind}
                data-testid="add-block-kind"
                style={{ width: 200 }}
                onChange={(value) => {
                  setNewBlockKind(value ?? 'manual_text');
                }}
              />
              <Button
                size="compact-xs"
                variant="default"
                loading={addBlock.isPending}
                data-testid="add-block"
                onClick={() => {
                  if (pageId === null) return;
                  /*
                   * Стартовий вміст кладеться в поле, яке рендерер читає саме в цього виду, —
                   * або не кладеться взагалі, якщо вид збирається зі структури. Раніше сюди
                   * завжди йшло `{ text }`, і для FAQ це означало блок, якого немає в HTML.
                   */
                  const blockKind = newBlockKind as BlockKind;
                  const field = BLOCK_TEXT_FIELD[blockKind];
                  const base = emptyBlockContent(blockKind) as Record<string, unknown>;
                  const content =
                    field === null
                      ? base
                      : {
                          ...base,
                          [field]:
                            blockKind === 'ai_text' || blockKind === 'manual_text'
                              ? richTextFromPlain('Новий блок')
                              : 'Новий блок',
                        };
                  addBlock.mutate({
                    ...blockWrite(blockKind, content),
                    pageId,
                    expectedVersion: page.data?.version ?? 0,
                  });
                }}
              >
                Додати блок
              </Button>
              {addBlock.error === null ? null : (
                <Text className={classes.danger}>{publicMessageOf(addBlock.error)}</Text>
              )}
            </Group>
          </Box>

          {/*
           * Черга сторінки. Постановка повертає «прийнято» — і на цьому видимість
           * закінчувалась би: робота, що вичерпала спроби, лишається в таблиці зі своєю
           * помилкою, а без цього переліку єдиним симптомом була б назавжди порожня сторінка.
           */}
          <Box className={classes.panel} data-testid="editor-queue">
            <Text className={classes.panelTitle} mb={6}>
              Черга генерації
            </Text>
            {(queue.data?.items ?? []).length === 0 ? (
              <Text className={classes.dim} data-testid="queue-empty">
                Робіт у черзі немає
              </Text>
            ) : (
              <Stack gap={2}>
                {(queue.data?.items ?? []).map((job) => (
                  <Group key={job.id} gap={8} data-testid={`queue-job-${job.id}`}>
                    <Badge
                      size="xs"
                      color={job.status === 'dead' || job.status === 'failed' ? 'rose' : 'mint'}
                    >
                      {job.status}
                    </Badge>
                    <Text className={classes.mono}>{job.target ?? '—'}</Text>
                    {job.attempts > 0 ? (
                      <Text className={classes.dim}>спроб: {job.attempts}</Text>
                    ) : null}
                    {job.error === null ? null : (
                      <Text className={classes.danger} data-testid={`queue-error-${job.id}`}>
                        {job.error}
                      </Text>
                    )}
                  </Group>
                ))}
              </Stack>
            )}
          </Box>

          <Box className={classes.panel} data-testid="editor-versions">
            <Text className={classes.panelTitle} mb={6}>
              Версії
            </Text>
            {versions.data === undefined || versions.data.length === 0 ? (
              <Text className={classes.dim} data-testid="no-versions">
                Сторінку ще не публікували
              </Text>
            ) : (
              <Stack gap={4}>
                {versions.data.map((version) => (
                  <Group
                    key={version.id}
                    gap={10}
                    data-testid={`version-${String(version.version)}`}
                  >
                    <span className={classes.mono}>v{version.version}</span>
                    <Text className={classes.dim}>
                      {new Date(version.createdAt).toLocaleString('uk-UA', {
                        timeZone: 'Europe/Kyiv',
                      })}
                    </Text>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      data-testid={`revert-${String(version.version)}`}
                      onClick={() => {
                        if (pageId !== null && page.data !== undefined) {
                          revert.mutate({
                            id: pageId,
                            version: version.version,
                            expectedVersion: page.data.version,
                          });
                        }
                      }}
                    >
                      Відкотити
                    </Button>
                  </Group>
                ))}
              </Stack>
            )}
            {revert.error === null ? null : (
              <Text className={classes.danger} mt={6} data-testid="revert-error">
                {publicMessageOf(revert.error)}
              </Text>
            )}
          </Box>
        </>
      )}
    </Stack>
  );
}
